import { downloadCsv, toCsvRow } from '../../utils/downloadCsv';
import { getGymExportBrand, getGymExportSlug } from '../../utils/gymExportBrand';
import {
  getExportFileDate,
  getExportTimestamp,
} from '../../utils/professionalExport';
import {
  buildThemedPrintCss,
  buildDetailedReportPrintExtras,
  buildThemedExcelCss,
  wrapExcelHtmlDocument,
  excelEscape,
  excelNumber,
  excelTd,
  excelTh,
  excelBlankRow,
} from '../../utils/themeExport';
import {
  buildManualPettyCashDetail,
  buildPettyCashDetail,
} from './finance/financeData';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatAmount(value) {
  const n = Number(value) || 0;
  if (n <= 0) return '';
  return n.toLocaleString('en-US');
}

function formatAmountOrZero(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-US');
}

function resolvePettyDetail({
  reportDate,
  manualPettyCash,
  openingBalance,
  manualRows,
  payments,
  guestPassIncome,
  expenses,
  salesRows,
}) {
  if (manualPettyCash) {
    return buildManualPettyCashDetail({
      date: reportDate,
      openingBalance,
      manualRows,
    });
  }
  return buildPettyCashDetail({
    date: reportDate,
    payments,
    guestPassIncome,
    expenses,
    salesRows,
    openingBalance,
  });
}

function buildExportSections(payload) {
  const {
    gymTitle,
    reportDate,
    zReport,
  } = payload;
  const brand = getGymExportBrand();
  const title = payload.title || 'Detailed Report';
  const day = String(reportDate || '').slice(0, 10);
  const petty = resolvePettyDetail(payload);

  const saleBlocks = (zReport?.saleReportSections || []).map((section) => {
    const rows = (section.rows || []).map((row) => ([
      row.membershipCode || '',
      row.name || '',
      row.package || '',
      row.duration === '—' ? '' : (row.duration || ''),
      Number(row.amount) || 0,
      row.modeOfPayment || '',
      row.recNo === '—' ? '' : (row.recNo || ''),
      row.trainer || '',
    ]));
    return {
      title: section.title,
      headers: ['Membership Code', 'Name', 'Package', 'Duration', 'Amount', 'Mode of Payment', 'Rec NO.', 'Trainer'],
      rows,
      total: Number(section.total) || 0,
    };
  });

  const pettyRows = (petty?.rows || [])
    .filter((row) => row.kind !== 'opening' && row.kind !== 'closing')
    .map((row) => ([
      row.date || day,
      row.description || '',
      Number(row.receipts) || 0,
      Number(row.payments) || 0,
      Number(row.balance) || 0,
    ]));

  const grandTotal = Number(zReport?.saleReportGrandTotal)
    || Number(zReport?.saleReportTotalAmount)
    || saleBlocks.reduce((sum, b) => sum + (Number(b.total) || 0), 0);

  return {
    brand,
    gymTitle: gymTitle || brand.name,
    title,
    day,
    saleBlocks,
    paymentsTotal: grandTotal,
    salesTotal: grandTotal,
    grandTotal,
    petty: {
      openingBalance: Number(petty?.openingBalance) || 0,
      rows: pettyRows,
      totalReceipts: Number(petty?.totalReceipts) || 0,
      totalPayments: Number(petty?.totalPayments) || 0,
      closingBalance: Number(petty?.closingBalance) || 0,
    },
  };
}

function buildCsvContent(payload) {
  const data = buildExportSections(payload);
  const lines = [
    data.gymTitle,
    data.brand.branch || '',
    data.brand.address || '',
    [data.brand.phone, data.brand.email].filter(Boolean).join(' · '),
    data.title,
    `Report date: ${data.day}`,
    `Generated: ${getExportTimestamp()}`,
    '',
    'SALE REPORT',
  ];

  data.saleBlocks.forEach((block) => {
    lines.push('');
    lines.push(block.title);
    lines.push(toCsvRow(block.headers));
    if (block.rows.length) {
      block.rows.forEach((row) => lines.push(toCsvRow(row)));
    } else {
      lines.push(toCsvRow(['No sales for this date', '', '', '', '', '', '', '']));
    }
    lines.push(toCsvRow(['Total', '', '', '', block.total, '', '', '']));
  });

  lines.push('');
  lines.push(toCsvRow(['Grand Total', data.grandTotal]));

  lines.push('');
  lines.push('PETTY CASH DETAIL');
  lines.push(toCsvRow(['Date', 'Description', 'Receipts', 'Payments', 'Balance']));
  lines.push(toCsvRow(['', 'Opening Balance', '', '', data.petty.openingBalance]));
  data.petty.rows.forEach((row) => lines.push(toCsvRow(row)));
  lines.push(toCsvRow([
    '',
    'Closing Balance',
    data.petty.totalReceipts,
    data.petty.totalPayments,
    data.petty.closingBalance,
  ]));

  return lines.filter((line, i, arr) => !(line === '' && arr[i - 1] === '')).join('\n');
}

function buildSaleBlocksHtml(data) {
  const blocks = data.saleBlocks.map((block) => {
    const body = block.rows.length
      ? block.rows.map((row) => `
        <tr>
          <td>${escapeHtml(row[0])}</td>
          <td>${escapeHtml(row[1])}</td>
          <td>${escapeHtml(row[2])}</td>
          <td>${escapeHtml(row[3])}</td>
          <td class="num">${escapeHtml(formatAmount(row[4]) || '—')}</td>
          <td>${escapeHtml(row[5])}</td>
          <td>${escapeHtml(row[6])}</td>
          <td>${escapeHtml(row[7])}</td>
        </tr>`).join('')
      : `<tr><td colspan="8" class="empty">No sales for this date</td></tr>`;
    return `
      <div class="sale-block">
        <h3>${escapeHtml(block.title)}</h3>
        <table>
          <thead>
            <tr>
              <th>Membership Code</th>
              <th>Name</th>
              <th>Package</th>
              <th>Duration</th>
              <th>Amount</th>
              <th>Mode of Payment</th>
              <th>Rec NO.</th>
              <th>Trainer</th>
            </tr>
          </thead>
          <tbody>
            ${body}
            <tr class="total">
              <td colspan="4"><strong>Total</strong></td>
              <td class="num"><strong>${escapeHtml(formatAmount(block.total) || '—')}</strong></td>
              <td colspan="3"></td>
            </tr>
          </tbody>
        </table>
      </div>`;
  }).join('');
  return `<div class="sale-sections-grid">${blocks}</div>`;
}

function buildPettyBodyHtml(data) {
  if (!data.petty.rows.length) {
    return `<tr><td colspan="5" class="empty">No petty cash rows</td></tr>`;
  }
  return data.petty.rows.map((row) => `
    <tr>
      <td>${escapeHtml(row[0])}</td>
      <td>${escapeHtml(row[1])}</td>
      <td class="num">${escapeHtml(formatAmount(row[2]))}</td>
      <td class="num">${escapeHtml(formatAmount(row[3]))}</td>
      <td class="num"><strong>${escapeHtml(formatAmountOrZero(row[4]))}</strong></td>
    </tr>`).join('');
}

/** Print / PDF — sales on page 1 (A4), petty cash on page 2. */
function buildPrintHtmlContent(payload) {
  const data = buildExportSections(payload);
  const saleHtml = buildSaleBlocksHtml(data);
  const pettyBody = buildPettyBodyHtml(data);

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(data.title)} — ${escapeHtml(data.day)}</title>
  <style>
    ${buildThemedPrintCss(buildDetailedReportPrintExtras(), { compact: true })}
  </style>
</head>
<body>
  <div class="sale-page">
    <div class="theme-header">
      <div class="theme-brand">${escapeHtml(data.gymTitle)}</div>
      <div class="theme-title-row">
        <span class="theme-subtitle">SALE REPORT</span>
        <span class="theme-date">${escapeHtml(data.day)}</span>
      </div>
    </div>

    ${saleHtml}
    <table>
      <tbody>
        <tr class="grand">
          <td colspan="4"><strong>Grand Total</strong></td>
          <td class="num"><strong>${escapeHtml(formatAmountOrZero(data.grandTotal))}</strong></td>
          <td colspan="3"></td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="petty-page">
    <div class="theme-header">
      <div class="theme-brand">${escapeHtml(data.gymTitle)}</div>
      <div class="theme-title-row">
        <span class="theme-subtitle">PETTY CASH DETAIL</span>
        <span class="theme-date">${escapeHtml(data.day)}</span>
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Description</th>
          <th>Receipts</th>
          <th>Payments</th>
          <th>Balance</th>
        </tr>
      </thead>
      <tbody>
        <tr class="total">
          <td></td>
          <td><strong>Opening Balance</strong></td>
          <td class="num"></td>
          <td class="num"></td>
          <td class="num"><strong>${escapeHtml(formatAmountOrZero(data.petty.openingBalance))}</strong></td>
        </tr>
        ${pettyBody}
        <tr class="grand">
          <td></td>
          <td><strong>Closing Balance</strong></td>
          <td class="num"><strong>${escapeHtml(formatAmountOrZero(data.petty.totalReceipts))}</strong></td>
          <td class="num"><strong>${escapeHtml(formatAmountOrZero(data.petty.totalPayments))}</strong></td>
          <td class="num"><strong>${escapeHtml(formatAmountOrZero(data.petty.closingBalance))}</strong></td>
        </tr>
      </tbody>
    </table>
  </div>
</body>
</html>`;
}

/** Excel — one flat sheet: sale blocks (8 cols) then petty (5 cols aligned left). */
function buildExcelHtmlContent(payload) {
  const data = buildExportSections(payload);
  const e = excelEscape;

  const saleRows = [];
  data.saleBlocks.forEach((block) => {
    saleRows.push(`<tr>${excelTh(e(block.title), { colspan: 8 })}</tr>`);
    saleRows.push(`<tr>
      ${excelTh('Membership Code')}
      ${excelTh('Name')}
      ${excelTh('Package')}
      ${excelTh('Duration')}
      ${excelTh('Amount')}
      ${excelTh('Mode of Payment')}
      ${excelTh('Rec NO.')}
      ${excelTh('Trainer')}
    </tr>`);
    if (block.rows.length) {
      block.rows.forEach((row) => {
        saleRows.push(`<tr>
          ${excelTd(e(row[0]))}
          ${excelTd(e(row[1]))}
          ${excelTd(e(row[2]))}
          ${excelTd(e(row[3]))}
          ${excelTd(excelNumber(row[4]), { num: true })}
          ${excelTd(e(row[5]))}
          ${excelTd(e(row[6]))}
          ${excelTd(e(row[7]))}
        </tr>`);
      });
    } else {
      saleRows.push(`<tr>${excelTd('No sales for this date', { colspan: 8, center: true })}</tr>`);
    }
    saleRows.push(`<tr>
      ${excelTd('Total', { tot: true, colspan: 4, bold: true })}
      ${excelTd(excelNumber(block.total), { num: true, tot: true, bold: true })}
      ${excelTd('', { tot: true, colspan: 3 })}
    </tr>`);
    saleRows.push(excelBlankRow(8));
  });

  const pettyLines = [];
  pettyLines.push(`<tr>
    ${excelTh('Date')}
    ${excelTh('Description')}
    ${excelTh('Receipts')}
    ${excelTh('Payments')}
    ${excelTh('Balance')}
    ${excelTd('', { colspan: 3 })}
  </tr>`);
  pettyLines.push(`<tr>
    ${excelTd('')}
    ${excelTd('Opening Balance', { tot: true, bold: true })}
    ${excelTd('', { num: true, tot: true })}
    ${excelTd('', { num: true, tot: true })}
    ${excelTd(excelNumber(data.petty.openingBalance), { num: true, tot: true, bold: true })}
    ${excelTd('', { colspan: 3 })}
  </tr>`);
  if (data.petty.rows.length) {
    data.petty.rows.forEach((row) => {
      pettyLines.push(`<tr>
        ${excelTd(e(row[0]))}
        ${excelTd(e(row[1]))}
        ${excelTd(row[2] ? excelNumber(row[2]) : '', { num: true })}
        ${excelTd(row[3] ? excelNumber(row[3]) : '', { num: true })}
        ${excelTd(excelNumber(row[4]), { num: true, bold: true })}
        ${excelTd('', { colspan: 3 })}
      </tr>`);
    });
  } else {
    pettyLines.push(`<tr>${excelTd('No petty cash rows', { colspan: 5, center: true })}${excelTd('', { colspan: 3 })}</tr>`);
  }
  pettyLines.push(`<tr>
    ${excelTd('')}
    ${excelTd('Closing Balance', { tot: true, bold: true })}
    ${excelTd(excelNumber(data.petty.totalReceipts), { num: true, tot: true, bold: true })}
    ${excelTd(excelNumber(data.petty.totalPayments), { num: true, tot: true, bold: true })}
    ${excelTd(excelNumber(data.petty.closingBalance), { num: true, tot: true, bold: true })}
    ${excelTd('', { colspan: 3 })}
  </tr>`);

  const body = `
<table class="sheet">
  <colgroup>
    <col style="width:12%" />
    <col style="width:16%" />
    <col style="width:14%" />
    <col style="width:10%" />
    <col style="width:10%" />
    <col style="width:14%" />
    <col style="width:10%" />
    <col style="width:14%" />
  </colgroup>
  <tr>${excelTh(e(data.gymTitle), { colspan: 8, cls: 'hdr' })}</tr>
  <tr>
    ${excelTh('SALE REPORT', { colspan: 5, cls: 'subhdr' })}
    ${excelTh(e(data.day), { colspan: 3, cls: 'subhdr' })}
  </tr>
  ${excelBlankRow(8)}
  ${saleRows.join('')}
  <tr>
    ${excelTd('Grand Total', { tot: true, colspan: 4, bold: true })}
    ${excelTd(excelNumber(data.grandTotal), { num: true, tot: true, bold: true })}
    ${excelTd('', { tot: true, colspan: 3 })}
  </tr>
  ${excelBlankRow(8)}
  ${excelBlankRow(8)}
  <tr>${excelTh(e(data.gymTitle), { colspan: 8, cls: 'hdr' })}</tr>
  <tr>
    ${excelTh('PETTY CASH DETAIL', { colspan: 5, cls: 'subhdr' })}
    ${excelTh(e(data.day), { colspan: 3, cls: 'subhdr' })}
  </tr>
  ${excelBlankRow(8)}
  ${pettyLines.join('')}
</table>`;

  return wrapExcelHtmlDocument({
    title: `${data.title} — ${data.day}`,
    sheetName: 'Detailed Report',
    bodyHtml: body,
    css: buildThemedExcelCss(),
    fitWidth: 1,
    fitHeight: 0,
  });
}

function openHtml(html, { autoPrint = false } = {}) {
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  if (autoPrint) {
    const run = () => win.print();
    win.onload = run;
    window.setTimeout(run, 500);
  }
  return true;
}

function downloadBlob(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 200);
}

function fileBase(payload) {
  const slug = getGymExportSlug() || 'gym';
  const day = String(payload.reportDate || getExportFileDate()).slice(0, 10);
  return `detailed-report-${slug}-${day}`;
}

export function exportDetailedReportCsv(payload) {
  downloadCsv(buildCsvContent(payload), `${fileBase(payload)}.csv`);
}

export function exportDetailedReportExcel(payload) {
  downloadBlob(`\ufeff${buildExcelHtmlContent(payload)}`, `${fileBase(payload)}.xls`, 'application/vnd.ms-excel;charset=utf-8;');
}

export function exportDetailedReportPdf(payload) {
  return openHtml(buildPrintHtmlContent(payload), { autoPrint: false });
}

/** Used by on-screen Print for Detailed Report (sales page + petty page). */
export function buildDetailedReportPrintHtml(payload) {
  return buildPrintHtmlContent(payload);
}
